/// Renders queued takeoffs as small dots behind each runway threshold,
/// pointing away from the active takeoff direction. Mirrors iOS's
/// `takeoffQueueNodeLayer` (SpriteKit) rendering of `gvm.takeoffQueues`.
///
/// Layer is `update(entries)`-driven from `RadarScene`'s onTick callback
/// — the per-tick handler reads `session.takeoffQueueEntries()` and
/// passes the flat list here. We group by runway internally (the
/// session emits flat entries since promotion / deadline ticking
/// don't care about ordering) and position each dot along the
/// reverse-heading vector by its index within the runway's queue.
///
/// Node lifecycle matches AircraftLayer's: keep a Map<id, Graphics>
/// so the per-tick diff is O(n) — add nodes for ids we haven't seen,
/// remove nodes whose id has fallen off the entries list, mutate
/// position on the survivors.

import { Container, Graphics, Text } from 'pixi.js'
import type { PendingAircraft } from '../game/PendingAircraft'
import { radarTheme } from './theme'

interface DotNode {
  root: Container
  body: Graphics
  label: Text
}

const DOT_RADIUS = 3
const DOT_SPACING_PX = 12
const LABEL_OFFSET_PX = 7

export class TakeoffQueueLayer extends Container {
  private nodes = new Map<string, DotNode>()

  update(entries: readonly PendingAircraft[]): void {
    // Group entries by runway so we can position each dot at the
    // correct distance behind its threshold. The flat list preserves
    // insertion order within each queue (session writes pushes onto
    // the per-runway array, the flattening iterates that array), so
    // the per-runway index here matches the head-to-tail order of
    // the queue: index 0 is closest to the threshold.
    const byRunway = new Map<string, PendingAircraft[]>()
    for (const p of entries) {
      const key = `${p.airportName}-${p.runwayId}`
      const list = byRunway.get(key) ?? []
      list.push(p)
      byRunway.set(key, list)
    }

    const seen = new Set<string>()
    for (const [, queue] of byRunway) {
      for (let i = 0; i < queue.length; i++) {
        const pending = queue[i]!
        seen.add(pending.id)
        let node = this.nodes.get(pending.id)
        if (!node) {
          node = this.createNode(pending)
          this.nodes.set(pending.id, node)
          this.addChild(node.root)
        }
        this.positionNode(node, pending, i)
      }
    }

    // Reap nodes whose pending entry has been promoted (became a real
    // aircraft) or aged out (timeToFirstWaypointSec hit zero).
    for (const [id, node] of this.nodes) {
      if (!seen.has(id)) {
        node.root.removeFromParent()
        node.root.destroy({ children: true })
        this.nodes.delete(id)
      }
    }
  }

  private createNode(pending: PendingAircraft): DotNode {
    const root = new Container()

    const body = new Graphics()
    body.circle(0, 0, DOT_RADIUS)
    body.fill({ color: radarTheme.runway, alpha: 0.85 })
    root.addChild(body)

    const label = new Text({
      text: pending.flightNumber,
      style: {
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 9,
        fill: radarTheme.runway,
      },
    })
    label.alpha = 0.75
    label.position.set(LABEL_OFFSET_PX, -LABEL_OFFSET_PX - 2)
    root.addChild(label)

    return { root, body, label }
  }

  /// Position a dot behind the threshold along the runway's reverse-
  /// heading vector. Index 0 sits closest to the threshold; later
  /// indexes step `DOT_SPACING_PX` further back per slot.
  ///
  /// Heading math: web's coordinate system is screen-Y-down. Magnetic
  /// heading 0° = north. Converting heading → screen angle uses
  /// `screenAngle = (heading - 90) * π/180` (matches `headingFromTo`
  /// in spawner.ts). Reverse vector is `(-cos, -sin)` of that.
  private positionNode(node: DotNode, pending: PendingAircraft, index: number): void {
    const screenAngle = ((pending.runwayHeadingDeg - 90) * Math.PI) / 180
    const reverseDx = -Math.cos(screenAngle)
    const reverseDy = -Math.sin(screenAngle)
    const offset = DOT_SPACING_PX * (index + 1)
    node.root.position.set(
      pending.threshold.x + reverseDx * offset,
      pending.threshold.y + reverseDy * offset,
    )
  }
}
